/**
 * Utilidades compartidas: diálogos (menú, búsqueda, carrito), peticiones a la
 * Section Rendering API, formato de precios y animación al hacer scroll.
 */

export const theme = window.Theme || { routes: {}, strings: {} };

export const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function debounce(fn, wait = 250) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

export function parseHTML(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

/** Añade section_id a una URL para la Section Rendering API. */
export function sectionUrl(url, sectionId) {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('section_id', sectionId);
  return target.toString();
}

export async function fetchSection(url, sectionId, options = {}) {
  const response = await fetch(sectionUrl(url, sectionId), options);
  if (!response.ok) throw new Error(String(response.status));
  return parseHTML(await response.text());
}

/** Anuncia un mensaje a los lectores de pantalla. */
export function announce(message) {
  const region = document.querySelector('[data-live-region]');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 50);
}

/** Formatea céntimos con el formato de moneda de la tienda. */
export function formatMoney(cents, format = theme.moneyFormat || '{{amount_with_comma_separator}} €') {
  const value = Number(cents) || 0;
  const pattern = /\{\{\s*(\w+)\s*\}\}/;
  const format_ = (decimals, thousands, decimal) => {
    const [int, dec] = (value / 100).toFixed(decimals).split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return dec ? grouped + decimal + dec : grouped;
  };
  const match = format.match(pattern);
  if (!match) return format;

  const formats = {
    amount: () => format_(2, ',', '.'),
    amount_no_decimals: () => format_(0, ',', '.'),
    amount_with_comma_separator: () => format_(2, '.', ','),
    amount_no_decimals_with_comma_separator: () => format_(0, '.', ','),
    amount_with_space_separator: () => format_(2, ' ', ','),
    amount_no_decimals_with_space_separator: () => format_(0, ' ', ','),
    amount_with_apostrophe_separator: () => format_(2, "'", '.'),
    amount_with_period_and_space_separator: () => format_(2, ' ', '.'),
  };
  const formatter = formats[match[1]] || formats.amount;
  return format.replace(pattern, formatter());
}

/* Diálogos -------------------------------------------------------------- */

function setExpanded(id, expanded) {
  document
    .querySelectorAll(`[data-open-dialog="${id}"]`)
    .forEach((opener) => opener.setAttribute('aria-expanded', String(expanded)));
}

export function openDialog(dialog) {
  if (!dialog) return;
  if (!dialog.open) dialog.showModal();
  if (dialog.id) setExpanded(dialog.id, true);
  const autofocus = dialog.querySelector('[data-search-input]');
  if (autofocus) autofocus.focus();
  if (!dialog.dataset.bound) {
    dialog.dataset.bound = 'true';
    dialog.addEventListener('close', () => {
      if (dialog.id) setExpanded(dialog.id, false);
    });
  }
}

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-open-dialog]');
  if (opener) {
    const dialog = document.getElementById(opener.dataset.openDialog);
    if (dialog) {
      event.preventDefault();
      openDialog(dialog);
    }
    return;
  }

  const closer = event.target.closest('[data-close-dialog]');
  if (closer) {
    closer.closest('dialog')?.close();
    return;
  }

  // Clic en el fondo oscuro: cierra el diálogo.
  if (event.target instanceof HTMLDialogElement && event.target.open) {
    const rect = event.target.getBoundingClientRect();
    const inside =
      event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
    if (!inside) event.target.close();
  }
});

/* Animación suave al entrar en pantalla -------------------------------- */

function initReveal() {
  if (!document.body.classList.contains('has-animations')) return;
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -40px 0px' }
  );

  const selector =
    '.section .section__title, .product-grid__item, .collection-card, .benefit, .routine__step, .image-text__grid, .carousel__item';
  document.querySelectorAll(selector).forEach((element) => {
    // Solo lo que está por debajo de la pantalla, para no ocultar nada al cargar.
    if (element.getBoundingClientRect().top > window.innerHeight) {
      element.classList.add('reveal');
      observer.observe(element);
    }
  });
}

initReveal();
